import React, { useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLicenseGate } from "../../../hooks/useLicenseGate";
import { RootState, useAppDispatch } from "../../../store";
import { hideModal } from "../../../store/actions/modalActions";
import { useTranslation } from "react-i18next";
import { LicensePlan, isRenewalLocked } from "../../../store/actions/PlanActions";
import { RENEWAL_CATEGORY, renewalDescription } from "./renewal";
import { UPGRADE_PATH, sectionLabel } from "../../../config/entitlementKeys";
import LicenseGateModal from "../../Settings/Licensing/LicenseGateModal";

/**
 * LicenseOverlay - wraps page content and renders a semi-transparent
 * overlay when the current route's section is not licensed.
 *
 * - Content still renders (blurred/grayed) behind the overlay so users
 *   can see what they're missing - enticing them to upgrade.
 * - All interactions are blocked (pointer-events-none on content,
 *   overlay captures clicks).
 * - What sits on top is `LicenseGateModal` - the SAME component every other
 *   gate shows, so the page lock, an action gate and a server 403 all offer
 *   the same two ways out: an admin upgrades, a member requests.
 *   A global flag (__ponsLicenseOverlayActive) tells interceptors and error
 *   handlers to suppress the duplicate they would otherwise raise on top.
 */
const LicenseOverlay: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { sectionForRoute, hasSectionAccess, enforced } = useLicenseGate();
  const addons = useSelector((state: RootState) => state.license?.addons ?? []);
  /* "Renew", not "your plan does not include X" - read-only (legacy expired
     included) or an unknown state flagged needs_renewal. Not grace. */
  const isExpired = useSelector((state: RootState) => isRenewalLocked(state.license?.access));
  const isOrgAdmin = useSelector(
    (state: RootState) => state.user?.userInfo?.is_org_admin === true
  );
  const licenseModalOpen = useSelector(
    (state: RootState) =>
      state.modal?.showModal === true && state.modal?.type === "license-request"
  );

  const section = sectionForRoute(location.pathname);
  const isLocked = enforced && section ? !hasSectionAccess(section) : false;

  /*
    NO LOAD-TIME HOLD.

    This used to hold every section-mapped page behind a spinner until the
    licence payload arrived. With the switch now coming from the backend
    (`isLicensingEnforced`), "not loaded" means "not enforced", so a missing or
    failed `/licensing/my-access` can no longer spin a page forever - which is
    exactly what it did on EU, where the fetch was skipped.

    The cost is the race the hold existed for: a page can mount, take a 403
    and raise the global upgrade modal before entitlements land. If the page
    then locks, the effect below closes that modal, so the user sees one lock,
    not two.
  */
  // Find the addon that unlocks this section
  const matchingAddon = useMemo(() => {
    if (!isLocked || !section) return null;
    return addons.find((a: LicensePlan) => a.feature_gates?.sections?.includes(section)) ?? null;
  }, [isLocked, section, addons]);

  /* The label comes from the canonical vocabulary, like the keys do - this
     component used to carry a third copy of the map, keyed on the pre-rename
     spellings. */
  const label = section ? sectionLabel(section) : "";

  /*
    Set global flag so apis.tsx interceptors and LicenseErrorHandler suppress
    duplicate modals / error toasts on locked pages.

    Computed in RENDER, not only in an effect - a 403 can land before effects
    flush, and the guard has to already read true by then.
  */
  const overlayActive = isLocked;
  (window as any).__ponsLicenseOverlayActive = overlayActive;

  useEffect(() => {
    (window as any).__ponsLicenseOverlayActive = overlayActive;
    return () => {
      (window as any).__ponsLicenseOverlayActive = false;
    };
  }, [overlayActive]);

  /* The page locked AFTER a 403 raised the global modal: the overlay owns the
     lock UX now, so close the duplicate. */
  useEffect(() => {
    if (isLocked && licenseModalOpen) dispatch(hideModal());
  }, [isLocked, licenseModalOpen, dispatch]);

  /*
    NOT LOCKED, NOTHING TO SAY.

    THIS LINE WAS DELETED IN 735ef8c2 and it is the whole of the "every page
    shows the upgrade modal" bug. That commit replaced the overlay's bespoke
    card with the shared `LicenseGateModal` and rewrote the render block —
    dropping the early return with it. `isLocked` went on being computed, and
    went on driving the add-on lookup and the `__ponsLicenseOverlayActive`
    flag, so it read as live and reviewed as fine; nothing consulted it before
    drawing the scrim. Every page dimmed itself and put an upgrade modal over
    the top, on any plan, whatever the entitlements said - which is why a firm
    on PONS Full with all 21 sections saw it, and why routes with no section
    at all (`/home`) showed a modal with a blank name.

    A gate that is computed but not consulted is not a gate.
  */
  if (!isLocked) return <>{children}</>;

  /*
    THE LOCKED PAGE IS NOT RENDERED AT ALL.

    It used to render behind the scrim, dimmed and inert. The data was 403'd so
    the tables came up empty, but the page still MOUNTED: it ran its effects,
    fired its fetches, and left a working shell one DevTools node-delete away
    from looking like the product. The overlay is UX, not the security
    boundary - the server refuses regardless - but there is no reason to build
    the room behind the locked door.

    So a locked route renders the lock and nothing else. No children, no
    effects, no requests.
  */
  return (
    <div className="relative flex-1 min-h-0" data-testid="license-locked-shell">
      {/* Deliberately empty: the page it replaces is not mounted. */}
      <div className="absolute inset-0 bg-bg" aria-hidden />

      {/*
        CENTRED ON THE SCREEN, not in the content.

        The scrim is `absolute inset-0`, which on a SCROLLABLE page is as tall
        as the page's content - a long table, in the case this was reported on
        - so centring the card inside it put the card halfway down the table
        rather than halfway down the screen. The inner box is `sticky top-0`
        at one viewport high, so wherever the reader is scrolled, the card is
        centred in what they can actually see.
      */}
      {/*
        SCOPED TO THE CONTENT REGION, not the viewport.

        `<main>` and `<Sidebar />` are siblings in a flex row (`PrivateLayout`),
        so `absolute inset-0` here reaches the right-hand column only and the
        sidebar stays clickable - which is what a locked page most needs, since
        the way out of it is another section. The full-viewport lock the report
        hit was the GLOBAL modal, raised by an API 403 on a route this overlay
        did not cover; those routes are mapped now.
      */}
      <div className="absolute inset-0 z-30 bg-white/30 dark:bg-gray-900/30">
        <div className="sticky top-0 h-screen max-h-screen flex items-center
                        justify-center overflow-y-auto py-8">
        <div className="glass max-w-md w-full mx-4 rounded-xl shadow-lg my-auto"
             data-testid="license-overlay-card">
          {isExpired ? (
            /* EXPIRED is not "your plan does not include X": there is no plan.
               Say so, and offer renewal (admin) or who to ask (member). */
            <LicenseGateModal
              title={t(label)}
              /* Not the read-only wording: this page cannot be VIEWED, so
                 "you can view existing data" would be untrue here. */
              description={renewalDescription({ isOrgAdmin, feature: t(label) })}
              category={RENEWAL_CATEGORY}
              onViewPlans={() => history.push(UPGRADE_PATH)}
              onClose={() => history.push("/home")}
            />
          ) : (
          <LicenseGateModal
            title={label}
            /* `label` cannot be blank - see `sectionLabel` - so this sentence
               can no longer render as "does not include ." */
            description={t('Your current plan does not include <strong>{{feature}}</strong>.', {
              feature: t(label),
              interpolation: { escapeValue: false },
            })}
            planName={matchingAddon?.display_name}
            category={matchingAddon ? "addon" : "base"}
            section={section || undefined}
            onViewPlans={() => history.push(UPGRADE_PATH)}
            /* There is nothing to dismiss TO - the page behind is the locked
               one. "Maybe later" takes them somewhere they can use. */
            onClose={() => history.push("/home")}
          />
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseOverlay;
